import { useMemo } from 'react'
import { find } from 'lodash-es'
import shallow from 'zustand/shallow'
import { useAppStore } from '@stores'
import { useModelStore } from '@editorStores'

/**
 * Opacity for meshes whose GUID is not in the structural element type map (e.g. stiffening segments).
 * In focus / ghost mode, unmatched GUIDs are treated as transparent unless explicitly highlighted.
 */
const useGuidFocusTransparencyOpacity = (guid: string, baseOpacity = 1): number => {
  const translucent = useModelStore(state => state.translucent)
  const { guidToTransparent } = useModelStore(state => state.transparencyVisibilityModel, shallow)
  const transparentOpacity = useAppStore(state => state.transparentOpacityV2 ?? 0.1)

  return useMemo(() => {
    if (!translucent) return baseOpacity

    const isTransparentForGuid = guidToTransparent[guid]
    if (isTransparentForGuid !== undefined) {
      return isTransparentForGuid ? transparentOpacity : 1
    }

    return transparentOpacity
  }, [baseOpacity, guid, guidToTransparent, translucent, transparentOpacity])
}

export const resolveFocusGuidsForElement = (
  elementGuid: string,
  stiffeningIntervals: StiffeningSegment[],
): string[] => {
  const segment = find(stiffeningIntervals, ['guid', elementGuid])
  if (segment?.element_guid) {
    return [elementGuid, segment.element_guid]
  }
  return [elementGuid]
}

export default useGuidFocusTransparencyOpacity
